/**
 * @file handle desktop navigation flyouts
 * @author tobias.frei@notch-interactive.com
 *
 * @module modules/m01-1-desktop-navigation.js
 *
 */

 /* Imports */
// import helperDom from '../helpers/helper-dom.js';
import pubsub from '../libs/pubsub';

const instance = {},

    /**
     * Object with default values.
     * This values can be overridden by passing an options object when creating a knob instance.
     *
     * @typedef defaults
     */
    defaults = {
        mainElement: '.m-desktopnav',
        flyoutElement: '.desktopnav__flyout',
        metaComp: '.desktopmeta',
        breakpoint: '(min-width: 48em)',
        hooks: {
            mainnav: '[data-flyout-toggle]',
            close: '[data-close]',
            metanav: '.desktopmeta__item--dropdown [data-flyout-toggle]'
        },
        states: {
            active: 'is-active',
            animClose: 'anim-close',
            animOpen: 'anim-open',
            navSmall: 'navigation--small',
            metaSmall: 'metanav--small',
            navPrepare: 'navigation--prepare',
            navPrepareAfter: 'navigation--prepareafter',
            fastScroll: 'navigation--fastscroll',
            disableScroll: 'body--noscroll',
            disableScrollOff: 'body--noscroll-off'
        },
        scroll: {
            fastFlagStep: 300,
            triggerScrollDown: 66,
            triggerScrollUp: 140
        }
    };


/* private variables */
let settings,
    navModule,
    metaComp,
    navTriggers,
    metaNavTriggers,
    closeBtns,
    scrollTop = null,
    onScrollTopVal,
    onScrollTriggerDown,
    spacePadding,
    fixTimer,
    closeFlag = false,

    /* private methods */
    bindEvents = () => {
        // bind click triggers to nav items
        navTriggers.forEach((navTrigger) => {
            navTrigger.addEventListener('click', triggerNavEvent, false);
        });

        // bind click triggers to close buttons
        closeBtns.forEach((closeBtn) => {
            closeBtn.addEventListener('click', triggerNavEvent, false);
        });

        metaNavTriggers.forEach((metaNav) => {
            metaNav.addEventListener('click', triggerMetaNavEvent, false);
        });

        // bind scroll event to windows only in non preview
        window.addEventListener('scroll', onScroll, true);

        // bind toggle event to first navitem only in non preview
        if(navTriggers[0]) {
            navTriggers[0].addEventListener('transitionend', onToggleNav, false);
        }

        if(window === window.top) {
            // bind resize only in non preview
            window.addEventListener('resize', function() {
                // close menu on mobile size
                var mq = window.matchMedia( settings.breakpoint );
                if (!mq.matches) {
                    closeMenu();
                }
            }, true);
        }
    },
    unbindEvents = () => {
        navTriggers.forEach((navTrigger) => {
            navTrigger.removeEventListener('click', triggerNavEvent);
        });
        metaNavTriggers.forEach((navTrigger) => {
            navTrigger.removeEventListener('click', triggerNavEvent);
        });
        window.removeEventListener('scroll', onScroll, true);
    },
    openMenu = (navitem) => {
        // reset all active Items
        resetActiveItems();

        // mark whole meta item if meta selected
        if(itemIsMeta(navitem)) {
            metaComp.classList.add(settings.states.active);
        } else {
            metaComp.classList.remove(settings.states.active);
        }

        // watch out for upcoming animation end
        navitem.nextElementSibling.addEventListener('animationend', animationListener, false);

        // add animation open class to navitem
        navitem.classList.add(settings.states.animOpen);

        // set active state to navitem
        navitem.classList.add(settings.states.active);

        // clear other item states
        window.requestAnimationFrame(function() {
            resetItemsExcept(navitem);
        });

        // bind esc keydown
        document.onkeydown = function(e) {
            e = e || window.event;
            var isEscape = false;
            if ('key' in e) {
                isEscape = (e.key === 'Escape' || e.key === 'Esc');
            } else {
                isEscape = (e.keyCode === 27);
            }
            if (isEscape) {
                closeMenu();
            }
        };
    },
    closeMenu = () => {
        let openEls = [...navModule.querySelectorAll('.' + settings.states.animOpen + settings.hooks.mainnav)];

        // return if no flyouts open
        if(openEls.length < 1) {
            return false;
        }

        closeFlag = true;

        // trigger close anim
        openEls.forEach((openElement) => {
            openElement.nextElementSibling.addEventListener('animationend', animationListener, false);
            openElement.classList.add(settings.states.animClose);
        });

        // remove active state on metanav wrapper
        metaComp.classList.remove(settings.states.active);

        // bind scroll event to windows only in non preview
        window.addEventListener('scroll', onScroll, true);

        return true;
    },
    triggerNavEvent = (e) => {
        e.preventDefault();

        // get element from target
        let el = e.target.correspondingUseElement || e.target || e.srcElement;


        if(!el.classList.contains('desktopnav__item--dropdown')&&!el.classList.contains('desktopmeta__item--dropdown')) {
            el = el.closest('.desktopnav__item--dropdown') || el.closest('.desktopmeta__item--dropdown');
            el = el.querySelector('.desktopnav__itemlink') || el.querySelector('.desktopmeta__itemlink');
        }

        // check if navitem already open and close if
        if( el.classList.contains(settings.states.active) ) {
            closeMenu();
            return;
        } else {
            if(scrollTop===null) {
                // store scrolltop position
                scrollTop = window.pageYOffset;
                getSpacePadding();

                window.requestAnimationFrame(function() {
                    document.documentElement.classList.add(settings.states.disableScroll);
                    setSpacePadding();
                });
            }

            // unbind scroll event on window
            window.removeEventListener('scroll', onScroll, true);

            openMenu(el);
        }

    },
    triggerMetaNavEvent = () => {
        // trigger pubsub event
        pubsub.trigger('desktopnav.open');
    },
    getSpacePadding = () => {
        spacePadding = window.innerWidth - document.documentElement.clientWidth;
    },
    setSpacePadding = () => {
        navModule.style.paddingRight = spacePadding + 'px';
        document.body.style.paddingRight = spacePadding + 'px';
    },
    clearSpacePadding = () => {
        navModule.style.paddingRight = '0px';
        document.body.style.paddingRight = '0px';
    },
    resetItemsExcept = (negligibleElement) => {
        let openEls = [...navModule.querySelectorAll('.' + settings.states.animOpen + settings.hooks.mainnav)];

        // return if no flyouts open
        if(openEls.length < 1) {
            return false;
        }

        // remove open-state on all open flyouts expect the active
        openEls.forEach((openElement) => {
            if(openElement.isEqualNode(negligibleElement)) {
                return;
            }
            openElement.classList.remove(settings.states.animOpen);
        });

        return true;
    },
    resetActiveItems = () => {
        let activeEls = [...navModule.querySelectorAll('.' + settings.states.active + settings.hooks.mainnav)];

        // if no flyouts open
        if(activeEls.length < 1) {
            return false;
        }

        // remove states on each active item
        activeEls.forEach((actElement) => {
            actElement.classList.remove(settings.states.active);

            if(itemIsMeta(actElement)) {
                metaComp.classList.remove(settings.states.active);
            }
        });

        return true;
    },
    resetAllItems = () => {
        // remove states on all items
        navTriggers.forEach((el) => {
            el.classList.remove(settings.states.animOpen);
            el.classList.remove(settings.states.active);
            el.classList.remove(settings.states.animClose);
        });

        return true;
    },
    onScroll = () => {
        onScrollTopVal = (window.pageYOffset || document.scrollTop)  - (document.clientTop || 0);
        onScrollTriggerDown = settings.scroll.triggerScrollDown;

        if(isNaN(onScrollTopVal)) {
            onScrollTopVal = 0;
        }

        if(onScrollTopVal >= onScrollTriggerDown) {
            // scroll down

            clearTimeout(fixTimer);
            navModule.querySelector('.desktopmeta__itemwrap').addEventListener('transitionend', transitionListener, false);
            document.body.classList.add(settings.states.navPrepare);

        } else {

            clearTimeout(fixTimer);
            navModule.querySelector('.desktopmeta__itemwrap').addEventListener('transitionend', transitionListener, false);
            document.body.classList.remove(settings.states.navSmall);
            document.body.classList.remove(settings.states.navPrepareAfter);

            fixTimer = setTimeout(function(){
                document.body.classList.remove(settings.states.navPrepare);
                document.body.classList.remove(settings.states.navPrepareAfter);
                document.body.classList.remove(settings.states.navSmall);
            }, 1000);
        }
    },
    onToggleNav = (e) => {
        if (e.propertyName === 'height' && e.type.toLowerCase().indexOf('transitionend') >= 0 && e.target.offsetHeight > 140) {
            e.target.removeEventListener('transitionend', this, false);
            document.body.classList.remove(settings.states.metaSmall);
        }
    },
    itemIsMeta = (el) => {
        return el.classList.contains('desktopmeta__itemlink');
    },
    animationListener = (e) => {
        if (e.animationName === 'openFlyout' && e.type.toLowerCase().indexOf('animationend') >= 0) {
            e.target.removeEventListener('animationend', animationListener, false);
            // let activeLink = helperDom.prevSibling(e.target, 'desktopnav__itemlink') || helperDom.prevSibling(e.target, 'desktopmeta__itemlink');
            // make sure to reset other items on flyout open
            // resetItemsExcept(activeLink);
        } else if (e.animationName === 'closeFlyout' && e.type.toLowerCase().indexOf('animationend') >= 0) {
            e.target.removeEventListener('animationend', animationListener, false);

            // make sure to reset all items on flyout close
            resetAllItems();

            // reset body states when nav is closed
            if(closeFlag) {
                // remove disable scroll class
                document.documentElement.classList.remove(settings.states.disableScroll);

                // add background fadeout for smooth close animation
                document.documentElement.addEventListener('animationend', animationListener, false);
                document.documentElement.classList.add(settings.states.disableScrollOff);

                // clear scrollbar space
                clearSpacePadding();
                closeFlag = false;

                window.requestAnimationFrame(function() {
                    window.scrollTo(0, scrollTop);
                    scrollTop = null;
                });
            }
        } else if (e.animationName === 'removeScrollblocker') {
            // nav close is finish
            e.target.removeEventListener('animationend', this, false);
            document.documentElement.classList.remove(settings.states.disableScrollOff);
        }
    },
    transitionListener = (e) => {
        if(e.target.parentNode.classList.contains('desktopmeta__itemwrap')&&e.propertyName==='width'&&document.body.classList.contains(settings.states.navPrepare)) {
            e.target.removeEventListener('transitionend', transitionListener, false);
            document.body.classList.add(settings.states.navPrepareAfter);
            document.body.classList.add(settings.states.navSmall);
        } else if(e.target.parentNode.classList.contains('desktopmeta__itemwrap')&&e.propertyName==='top'&&!document.body.classList.contains(settings.states.navSmall)) {
            clearTimeout(fixTimer);
            document.body.classList.remove(settings.states.navPrepare);
        }
    };


/* public methods */

/**
 * Initialize module
 *
 * @param {object=} options - Override default settings with options object.
 * @param {string=} options.mainElement - The query selector of the main navigation.
 * @param {string=} options.metaComp - The query selector of the meta component.
 * @param {string=} options.breakpoint - The breakpoint for visible desktop nav.
 * @param {object=} options.hooks - The settings for all triggers.
 * @param {string=} options.hooks.mainnav - The query selector for flyout trigger.
 * @param {string=} options.hooks.close - The query selector for close trigger.
 * @param {object=} options.states - The classes of the switch states.
 * @param {string=} options.states.active - The classname of the active item state.
 * @param {string=} options.states.animClose - The classname for closing animation.
 * @param {string=} options.states.animOpen - The classname for opening animation.
 * @param {string=} options.states.navSmall - The modification class for small nav.
 * @param {string=} options.states.metaSmall - The modification class for small meta.
 * @param {string=} options.states.fastscroll - The modification class for fastscrolling.
 * @param {object=} options.scroll - The classes of the switch states.
 * @param {string=} options.scroll.fastFlagStep - The difference on each scrollevent to set fastscroll flag.
 * @param {string=} options.scroll.triggerScrollDown - The scrollposition for trigger on scroll down.
 * @param {string=} options.scroll.triggerScrollUp - The scrollposition for trigger on scroll up.
 * @return {object} Instance of created module.
 */
instance.init = (options) => {
    settings = Object.assign({}, defaults, options);

    // get module element
    navModule = document.querySelector(settings.mainElement);
    if(!navModule) {
        return;
    }

    // get triggers in nav
    navTriggers = [...navModule.querySelectorAll(settings.hooks.mainnav)];
    metaNavTriggers = [...navModule.querySelectorAll(settings.hooks.metanav)];
    if(navTriggers.length < 1) {
        return;
    }

    closeBtns = [...navModule.querySelectorAll(settings.hooks.close)];
    if(closeBtns.length < 1) {
        return;
    }


    // get meta component
    metaComp = navModule.querySelector(settings.metaComp);

    // bind triggers to event
    bindEvents();

    return instance;
};

/**
 * Destroy this module.
 * Unbind events, remove dom classes, etc...
 */
instance.destroy = () => {
    unbindEvents();
};


export default instance;
